import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setOperationalOutlet, setOperationalRecords } from "@/lib/operationalContext";
import { canUseOfflineWorkspace, clearWorkspaceDisplay, readWorkspaceDisplay, saveWorkspaceDisplay } from "@/lib/workspaceDisplayCache";

const OutletCtx = createContext({
  outlets: [],
  current: null,        // null = "All outlets" (consolidated)
  setCurrent: () => {},
  reload: () => {},
});

const LS_KEY = "ssv:current_outlet_id";

export function OutletProvider({ children }) {
  const { user } = useAuth();
  const actorKey = `${LS_KEY}:${user?.id || "guest"}`;
  const [outlets, setOutlets] = useState([]);
  const [current, setCurrentState] = useState(null);
  const actorRef = useRef(user?.id);
  actorRef.current = user?.id;

  const setCurrent = useCallback((outlet) => {
    setOperationalOutlet(outlet?.is_central_kitchen ? null : outlet?.id);
    setCurrentState(outlet);
    if (outlet?.id) localStorage.setItem(actorKey, outlet.id);
    else localStorage.removeItem(actorKey);
  }, [actorKey]);

  const reload = useCallback(async () => {
    const actorId = user?.id;
    const applyList = list => {
      if (actorRef.current !== actorId) return;
      setOutlets(list);
      setOperationalRecords(list);
      const scoped = ["staff", "cashier", "outlet_manager", "central_kitchen", "outlet"].includes(user.role);
      const savedId = localStorage.getItem(actorKey);
      const chosen = scoped ? list.find(o => o.id === user.outlet_id) || (list.length === 1 ? list[0] : null)
        : list.find(o => o.id === savedId) || null;
      setOperationalOutlet(chosen?.is_central_kitchen ? null : chosen?.id);
      setCurrentState(chosen);
    };
    try {
      const r = await api.get("/outlets");
      const list = r.data.outlets || [];
      if (actorRef.current !== actorId) return;
      saveWorkspaceDisplay(actorId, "outlets", list).catch(() => {});
      applyList(list);
    } catch (e) {
      if (actorRef.current !== actorId) return;
      if ([401, 403].includes(e.response?.status)) await clearWorkspaceDisplay(actorId).catch(() => {});
      const cached = !e.response && canUseOfflineWorkspace(user, window.location.pathname)
        ? await readWorkspaceDisplay(actorId, "outlets").catch(() => null) : null;
      applyList(cached || []);
    }
  }, [user, actorKey]);

  useEffect(() => {
    // Only fetch outlets once the user is authenticated.
    // user === null → still checking, user === false → unauth, object → auth
    if (user && user !== false && typeof user === "object") {
      reload();
    } else if (user === false) {
      setOutlets([]);
      setCurrentState(null);
      setOperationalOutlet(null);
      setOperationalRecords([]);
    }
    const reconnect = () => { if (user?.id) reload(); };
    window.addEventListener("online", reconnect);
    return () => window.removeEventListener("online", reconnect);
  }, [user]);

  const value = useMemo(() => ({ outlets, current, setCurrent, reload }), [outlets, current, setCurrent, reload]);

  return (
    <OutletCtx.Provider value={value}>
      {children}
    </OutletCtx.Provider>
  );
}

export const useOutlet = () => useContext(OutletCtx);
